import * as React from "react";
import { Autocomplete, Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import type { LlmRole, LlmSettings } from "../../models/domain";
import { activeProviders, declaredModels, roleProblems } from "./llmCatalog";

// Une cellule vide affiche la valeur HÉRITÉE du global, en grisé.
const inheritedSx = { "& input::placeholder": { color: "text.disabled", opacity: 1, fontStyle: "italic" } };

interface Props {
  role: LlmRole;
  llm: LlmSettings;
  /** Valeurs posées à CE niveau ("" = non posée). */
  provider: string;
  model: string;
  /** Matière : ce qui vaut quand la cellule est vide (le global). Absent = le global lui-même. */
  inherited?: { provider?: string; model?: string };
  onProvider: (v: string) => void;
  onModel: (v: string) => void;
  /** Le modèle tapé n'est pas déclaré : ouvrir « Déclarer un modèle » sur ce couple. */
  onDeclare: (provider: string, model: string) => void;
  label: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

/**
 * Fournisseur (liste des actifs) et modèle (texte libre, en suggestion les
 * modèles déclarés que ce rôle sait appeler). Un modèle inconnu propose « Déclarer… » ; un couple qui ne
 * convient pas au rôle le dit tout de suite (le serveur le refusera).
 */
export const ModelPicker: React.FC<Props> = ({
  role,
  llm,
  provider,
  model,
  inherited,
  onProvider,
  onModel,
  onDeclare,
  label,
  size = "medium",
  disabled,
}) => {
  const effProvider = provider || inherited?.provider || "";
  const effModel = model.trim() || inherited?.model || "";
  const providers = activeProviders(llm);
  // Un fournisseur posé puis désactivé reste affiché (le serveur refusera
  // l'enregistrement tant qu'il est choisi).
  const current = provider && !providers.some((p) => p.id === provider) ? provider : null;
  const touched = Boolean(provider || model.trim());
  const problems = touched ? roleProblems(llm, role, effProvider, effModel) : [];

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        <TextField
          select
          size={size}
          disabled={disabled}
          value={provider}
          onChange={(e) => onProvider(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          inputProps={{ "aria-label": `Fournisseur — ${label}` }}
          sx={{ minWidth: size === "small" ? 110 : 150, ...inheritedSx }}
        >
          {inherited ? (
            <MenuItem value="">
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                {llm.providers?.[inherited.provider ?? ""]?.label ?? inherited.provider ?? "global"}
              </Typography>
            </MenuItem>
          ) : (
            <MenuItem value="" disabled>
              Fournisseur
            </MenuItem>
          )}
          {providers.map(({ id, provider: p }) => (
            <MenuItem key={id} value={id}>
              {p.label}
            </MenuItem>
          ))}
          {current && (
            <MenuItem value={current}>
              {llm.providers?.[current]?.label ?? current} (désactivé)
            </MenuItem>
          )}
        </TextField>
        <Autocomplete
          freeSolo
          size={size}
          disabled={disabled}
          options={effProvider ? declaredModels(llm, effProvider, role) : []}
          value={model}
          onInputChange={(_, v) => onModel(v)}
          sx={{ flex: 1, minWidth: size === "small" ? 140 : 180 }}
          renderInput={(p) => (
            <TextField
              {...p}
              placeholder={inherited?.model ?? "obligatoire"}
              sx={inheritedSx}
              inputProps={{ ...p.inputProps, "aria-label": `Modèle — ${label}` }}
            />
          )}
        />
      </Stack>
      {problems === undefined ? (
        <Box>
          <Typography variant="caption" color="warning.main" component="span">
            {effModel} n'est pas déclaré chez {llm.providers?.[effProvider]?.label ?? effProvider}.{" "}
          </Typography>
          <Button size="small" disabled={disabled} onClick={() => onDeclare(effProvider, effModel)}>
            Déclarer…
          </Button>
        </Box>
      ) : (
        problems.length > 0 && (
          <Typography variant="caption" color="error" component="div">
            {problems.join(" ; ")}.
          </Typography>
        )
      )}
    </Stack>
  );
};

export default ModelPicker;
